import React, { useEffect, useState } from "react";
import {
  Box,
  Tab,
  Tabs,
  Typography,
  Paper,
  Button,
  Card,
  CardContent,
  CardMedia,
  Grid,
  ListItem,
  IconButton,
  List,
  ListItemText,
} from "@mui/material";
import { CheckCircleIcon } from "lucide-react";

import { backendUrlImages } from "../../constant/config";
import httpService from "../../common/httpService";
import PaperSubmissionForm from "../../components/abstract/abstractUser";
import Welcome from "../../components/UI/Welcome";
import SpeakerV2 from "../../components/speaker/SpeakerV2";
import SinglePriceV2 from "../../components/price/SinglePriceV2";
import AccommodationPrices from "../../components/price/AccommodationPrices";
import SponserV1 from "../../components/client/SponserV1";
import InnerBanner from "../../components/OnePageComponent/InnerBanner";

const ConferenceDetails = () => {
  // conferenceId from URL last segment: /conference/details/62 -> "62"
  const conferenceId = window.location.pathname.split("/").filter(Boolean).pop();
const backendBase = "https://eventscons.com/eventbackendnew/storage/app/public";

const makeStorageUrl = (pathOrUrl) => {
  if (!pathOrUrl) return null;

  const value = String(pathOrUrl).trim();

  if (/^https?:\/\//i.test(value)) {
    if (value.includes("/event/public/storage/")) {
      return value.replace(
        "https://eventscons.com/eventbackendnew/public/storage/",
        `${backendBase}/`
      );
    }

    if (value.includes("/event/storage/") && !value.includes("/event/storage/app/public/")) {
      return value.replace(
        "https://eventscons.com/eventbackendnew/storage/",
        `${backendBase}/`
      );
    }

    return value;
  }

  return `${backendBase}/${value.replace(/^\/+/, "")}`;
};
  const [selectedTab, setSelectedTab] = useState(0);
  const [data, setData] = useState({});
  const [spo, setSpo] = useState([]);
  const [work, setWork] = useState([]);
  const [roomPrices, setRoomPrices] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [value, setValue] = useState(0);
  const [stopScroll, setStopScroll] = useState(false);
  const tabsCount = 15;

  // Conference Book state
  const [conferenceBooks, setConferenceBooks] = useState([]);
  const [conferenceBookLoading, setConferenceBookLoading] = useState(false);
  const [conferenceBookError, setConferenceBookError] = useState(null);

  // Posters state
  const [posters, setPosters] = useState([]);
  const [postersLoading, setPostersLoading] = useState(false);
  const [postersError, setPostersError] = useState(null);

  // Countries state for flags
  const [countries, setCountries] = useState([]);

  const BaseUrl = process.env.REACT_APP_BASE_URL;
  const [info, setInfo] = useState(null);
  const [closingDate, setClosingDate] = useState(null); // Sirf registration ke liye

  // ✅ Countdown states
  const [countdownTarget, setCountdownTarget] = useState(null); // Date object
  const [countdown, setCountdown] = useState({ d: 0, h: 0, m: 0, s: 0, done: false });
  const [countdownError, setCountdownError] = useState(null);

  const handleChange = (event, newValue) => {
    setValue(newValue);
    setSelectedTab(newValue);
  };

  const handleMouseEnter = () => setStopScroll(true);
  const handleMouseLeave = () => setStopScroll(false);

  // Fetch countries API for flags
  const fetchCountries = async () => {
    try {
      const response = await fetch("https://eventscons.com/eventbackendnew/api/countries");
      const result = await response.json();
      setCountries(result.data || []);
    } catch (err) {
      console.error("Error fetching countries:", err);
    }
  };

  // ✅ ONLY REGISTRATION DATE - EXACT DATABASE TIME
  const fetchClosingDate = async () => {
    try {
      const apiUrl = `https://eventscons.com/eventbackendnew/api/closing/date/${conferenceId}`;
      const res = await fetch(apiUrl);
      const response = await res.json();

      if (response.success && response.data) {
        // Sirf type="registration" dhundo
        const regData = response.data.find((item) => item.type === "registration");

        if (regData) {
          const dbDateTime = regData.closing_date;

          let formattedDate;
          if (dbDateTime.includes("T")) {
            // UTC format
            const dateObj = new Date(dbDateTime);
            const utcHours = dateObj.getUTCHours().toString().padStart(2, "0");
            const utcMinutes = dateObj.getUTCMinutes().toString().padStart(2, "0");
            const utcDate = dateObj.toLocaleDateString("en-GB", { timeZone: "UTC" });
            formattedDate = `${utcDate}, ${utcHours}:${utcMinutes}`;
          } else {
            // MySQL format
            const [datePart, timePart] = dbDateTime.split(" ");
            const [year, month, day] = datePart.split("-");
            const [hours, minutes] = timePart.split(":").slice(0, 2);
            formattedDate = `${day}/${month}/${year}, ${hours}:${minutes}`;
          }

          setClosingDate(formattedDate);
        }
      }
    } catch (err) {
      console.log("Error fetching closing date:", err);
    }
  };

  // ✅ Countdown API call
  const fetchCountdown = async () => {
    try {
      setCountdownError(null);

      const res = await fetch(
        `https://eventscons.com/eventbackendnew/api/conferences-countdown/${conferenceId}`
      );
      const json = await res.json();

      const payload = json?.data ?? json;

      // 👇 API response me jo actual key ho, wo yahan set kar dena
      const raw =
        payload?.countdown_date ||
        payload?.end_date ||
        payload?.start_date ||
        payload?.date ||
        payload?.datetime ||
        payload?.conference_date ||
        payload?.closing_date;

      if (!raw) throw new Error("Countdown date not found in API response");

      // Raw can be "YYYY-MM-DD HH:mm:ss" OR ISO string
      const target = raw.includes("T") ? new Date(raw) : new Date(raw.replace(" ", "T"));

      if (Number.isNaN(target.getTime())) throw new Error("Invalid countdown date");

      setCountdownTarget(target);
    } catch (e) {
      console.error("Error fetching countdown:", e);
      setCountdownError("Failed to load countdown.");
      setCountdownTarget(null);
    }
  };

  // ✅ Countdown interval
  useEffect(() => {
    if (!countdownTarget) return;

    const tick = () => {
      const diff = countdownTarget.getTime() - Date.now();

      if (diff <= 0) {
        setCountdown({ d: 0, h: 0, m: 0, s: 0, done: true });
        return;
      }

      const d = Math.floor(diff / (1000 * 60 * 60 * 24));
      const h = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const m = Math.floor((diff / (1000 * 60)) % 60);
      const s = Math.floor((diff / 1000) % 60);

      setCountdown({ d, h, m, s, done: false });
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [countdownTarget]);

  // API calls
  const getWelcomeData = async () => {
    const getAuthToken = () => localStorage.getItem("token");
    try {
      await httpService({
        method: "GET",
        url: `${BaseUrl}/conferences/${conferenceId}/welcome-message`,
        headers: { Authorization: `Bearer ${getAuthToken()}` },
        showLoader: true,
        onSuccess: (data) => setInfo(data),
      });
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const getSponsor = async () => {
    const getAuthToken = () => localStorage.getItem("token");
    try {
      await httpService({
        method: "GET",
        url: `${BaseUrl}/logo/spo/${conferenceId}`,
        headers: { Authorization: `Bearer ${getAuthToken()}` },
        showLoader: true,
        onSuccess: (data) => setSpo(data?.data),
      });
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const getWorkShop = async () => {
    const getAuthToken = () => localStorage.getItem("token");
    try {
      await httpService({
        method: "GET",
        url: `${BaseUrl}/workshops/${conferenceId}`,
        headers: { Authorization: `Bearer ${getAuthToken()}` },
        showLoader: true,
        onSuccess: (data) => setWork(data?.data),
      });
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const fetchRoomPrices = async () => {
    try {
      await httpService({
        method: "GET",
        url: `${BaseUrl}/reservation/room/price/${conferenceId}`,
        showLoader: true,
        onSuccess: (data) => {
          if (data.success) setRoomPrices(data.data);
        },
      });
    } catch (err) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const fetchConferenceData = async () => {
    const token = localStorage.getItem("token");
    const response = await httpService({
      method: "GET",
      url: `${BaseUrl}/con/id/${conferenceId}`,
      headers: { Authorization: `Bearer ${token}` },
    });
    setData(response);
  };

  const fetchConferenceBooks = async () => {
    try {
      setConferenceBookLoading(true);
      setConferenceBookError(null);
      const res = await httpService({
        method: "GET",
        url: `https://eventscons.com/eventbackendnew/api/conference-book/${conferenceId}/all`,
        showLoader: true,
      });
      setConferenceBooks(res?.data || res?.data?.data || []);
    } catch (err) {
      console.error("Error fetching conference book:", err);
      setConferenceBookError("Failed to load conference book.");
    } finally {
      setConferenceBookLoading(false);
    }
  };

  // Posters: type === "poster" + token header
  const fetchPosters = async () => {
    try {
      const token = localStorage.getItem("token");

      setPostersLoading(true);
      setPostersError(null);

      const res = await httpService({
        method: "GET",
        url: `https://eventscons.com/eventbackendnew/api/papers/con/${conferenceId}`,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        showLoader: true,
      });

      const list = res?.data || res?.data?.data || [];
      const postersOnly = Array.isArray(list) ? list.filter((item) => item.type === "poster") : [];
      setPosters(postersOnly);
    } catch (err) {
      console.error("Error fetching posters:", err);
      setPostersError("Failed to load posters.");
    } finally {
      setPostersLoading(false);
    }
  };

  useEffect(() => {
    getSponsor();
    getWelcomeData();
    getWorkShop();
    fetchCountries(); // Add countries fetch
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (conferenceId) {
      fetchConferenceData();
      fetchRoomPrices();
      fetchConferenceBooks();
      fetchPosters();
      fetchClosingDate();
      fetchCountdown(); // ✅ countdown call
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conferenceId]);

  // Function to get flag URL by country name
  const getFlagUrl = (countryName) => {
    if (!countryName || !countries.length) return null;
    const country = countries.find(
      (c) =>
        c.name.toLowerCase() === countryName.toLowerCase() ||
        c.name.toLowerCase().includes(countryName.toLowerCase())
    );
    return country?.flag_url || null;
  };

  const sections = [
    // { label: "Overview", component: "overview" },
    { label: "Welcome", component: "Welcome" },
     { label: "Committees ", component: "committee" },
     { label: "Topics", component: "topics" },
    { label: "Speakers", component: "Speakers" },
     { label: "Brochure", component: "brochure", url: "conference_brochure_pdf_url" },
{ label: "WorkShops", component: "WorkShop" },
{
      label: "Scientific Program",
      component: "scientificProgram",
      url: "conference_scientific_program_pdf_url",
    },

    { label: "Agenda", component: "conferenceBook" },
    { label: "Abstracts", component: "Abstract" },
      { label: "Posters", component: "posters" },
    { label: "Register", component: "pricing" },
   
    { label: "Hotels", component: "accommodation" },
   
    
    { label: "Sponsor", component: "sponsor" },
    // {
    //   label: "First Announcement Document",
    //   component: "firstAnnouncement",
    //   url: "first_announcement_pdf_url",
    // },
    // {
    //   label: "Second Announcement Document",
    //   component: "secondAnnouncement",
    //   url: "second_announcement_pdf_url",
    // },
    
  
    
  ];

  const renderDocumentContent = (url, label) => (
    <>
      <section className={`pricing-section-two ${"alternate"}`}>
        <div className="anim-icons">
          <span className="icon icon-line-1"></span>
          <span className="icon icon-circle-1"></span>
          <span className="icon icon-dots"></span>
        </div>
        <div className="auto-container">
          <div className="outer-box">
            <Paper elevation={3} sx={{ padding: 3, textAlign: "center" }}>
              {url ? (
                <>
                  <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                    {label}
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 1, mb: 2 }}>
                    This document contains important details about {label}. Please
                    download it for more information.
                  </Typography>
                  <Button
                    variant="contained"
                    href={url}
                    download
                    sx={{ mt: 2, backgroundColor: "#9B1321" }}
                  >
                    Download {label}
                  </Button>
                </>
              ) : (
                <Typography variant="body1" color="text.secondary">
                  No document is currently available for this section.
                </Typography>
              )}
            </Paper>
          </div>
        </div>
      </section>
    </>
  );

  const renderContent = () => {
    const { conference, scientific_topics, prices, committee_members } = data;

    switch (sections[selectedTab].component) {
                 case "overview":
        return (
          <section className="about-section-two">
            <div className="anim-icons full-width">
              <span className="icon icon-circle-blue wow fadeIn"></span>
              <span className="icon icon-dots wow fadeInleft"></span>
            </div>
            <div className="auto-container">
              <div className="row">
                <div className="content-column col-lg-6 col-md-12 col-sm-12 order-2">
                  <div className="inner-column">
                    <div className="sec-title">
                      <span className="title">Conference</span>
                      <h2>{conference?.title}</h2>
                      <div className="text">{conference?.description}</div>
                    </div>
                    <div className="row">
                      <div className="about-block col-lg-6 col-md-6 col-sm-12">
                        <div className="inner-box">
                          <h4>
                            <span className="icon fa fa-map-marker-alt"></span>{" "}
                            Where
                          </h4>
                          <div className="text">{conference?.location}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="image-column col-lg-6 col-md-12 col-sm-12">
                  {data?.image_url ? (
                    <div className="image-box">
                      <figure className="image wow fadeIn">
                        <img
                          src={`${backendUrlImages}${data?.conference.image?.substring(
                            data?.conference.image.indexOf("/" + 1)
                          )}`}
                          alt="image"
                        />
                      </figure>
                    </div>
                  ) : (
                    <Typography variant="body1" color="text.secondary">
                      No image available.
                    </Typography>
                  )}
                </div>
              </div>
            </div>
          </section>
        );










             case "WorkShop":
        return (
          <div>
            {work?.map((itemOuter, indexOuter) => (
              <section
                key={indexOuter}
                className={`pricing-section-two ${"alternate"}`}
              >
                <div className="sec-title  text-center">
                  <h2
                    style={{
                      display: "inline-block",
                      borderBottom: "2px solid #9B1321",
                      paddingBottom: "10px",
                    }}
                  >
                    Workshop
                  </h2>
                </div>
                <div className="anim-icons">
                  <span className="icon icon-line-1"></span>
                  <span className="icon icon-circle-1"></span>
                  <span className="icon icon-dots"></span>
                </div>
                <div className="auto-container">
                  <div className="outer-box">
                    <Grid
                      container
                      spacing={3}
                      style={{
                        padding: "10px",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 9999,
                      }}
                    >
                      {work?.map((item, index) => (
                        <Grid item xs={12} md={10} key={index}>
                          <Card
                            sx={{
                              borderRadius: "12px",
                              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                              transition:
                                "transform 0.3s ease, box-shadow 0.3s ease",
                              marginBottom: "5vh",
                            }}
                          >
                            <CardContent
                              sx={{
                                backgroundColor: "#f9f9f9",
                                borderRadius: 2,
                                padding: 3,
                                boxShadow: 3,
                              }}
                            >
                              <Typography
                                color="text.secondary"
                                variant="body1"
                                sx={{
                                  whiteSpace: "pre-wrap",
                                  wordWrap: "break-word",
                                  lineHeight: 1.6,
                                  fontSize: "1.2rem",
                                }}
                              >
                                {item?.description}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </div>
                </div>
              </section>
            ))}
          </div>
        );

      case "sponsor":
        return <SponserV1 spo={spo} />;

      case "Welcome":
        return <Welcome />;

      case "Abstract":
        return <PaperSubmissionForm conferenceId={conferenceId} />;

      case "Speakers":
        return <SpeakerV2 conferenceId={conferenceId} />;

      case "topics":
        return (
          <section className={`pricing-section-two ${"alternate"}`}>
            <div className="sec-title  text-center">
              <h2
                style={{
                  display: "inline-block",
                  borderBottom: "2px solid #9B1321",
                  paddingBottom: "10px",
                }}
              >
                Scientific Topics
              </h2>
            </div>
            <div className="anim-icons">
              <span className="icon icon-line-1"></span>
              <span className="icon icon-circle-1"></span>
              <span className="icon icon-dots"></span>
            </div>
            <div className="auto-container">
              <div className="outer-box">
                <Grid container spacing={3} sx={{ justifyContent: "center" }}>
                  <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{ display: "flex", justifyContent: "center" }}
                  >
                    <List>
                      {scientific_topics
                        ?.slice(0, Math.ceil(scientific_topics.length / 2))
                        .map((topic) => (
                          <ListItem
                            key={topic.id}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 2,
                            }}
                          >
                            <IconButton sx={{ color: "black", mr: 2 }}>
                              <CheckCircleIcon />
                            </IconButton>
                            <ListItemText
                              primary={
                                <Typography variant="h6">{topic?.title}</Typography>
                              }
                              secondary={
                                <Typography variant="body2">
                                  {topic?.description}
                                </Typography>
                              }
                            />
                          </ListItem>
                        ))}
                    </List>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{ display: "flex", justifyContent: "center" }}
                  >
                    <List>
                      {scientific_topics
                        ?.slice(Math.ceil(scientific_topics.length / 2))
                        .map((topic) => (
                          <ListItem
                            key={topic.id}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 2,
                            }}
                          >
                            <IconButton sx={{ color: "black", mr: 2 }}>
                              <CheckCircleIcon />
                            </IconButton>
                            <ListItemText
                              primary={
                                <Typography variant="h6">{topic?.title}</Typography>
                              }
                              secondary={
                                <Typography variant="body2">
                                  {topic?.description}
                                </Typography>
                              }
                            />
                          </ListItem>
                        ))}
                    </List>
                  </Grid>
                </Grid>
              </div>
            </div>
          </section>
        );

      // ✅ ONLY REGISTRATION DATE SHOW (No Accommodation)
      case "pricing":
        return (
          <section className={`pricing-section-two ${"alternate"}`}>
            <div className="sec-title text-center">
              <h2
                style={{
                  display: "inline-block",
                  borderBottom: "2px solid #9B1321",
                  paddingBottom: "10px",
                }}
              >
                Prices
              </h2>

              {closingDate && (
                <div style={{ marginTop: "15px" }}>
                  <div
                    style={{
                      border: "1px solid #9B1321",
                      backgroundColor: "#FFF5F5",
                      borderRadius: "25px",
                      padding: "4px 20px",
                      display: "inline-block",
                      color: "#9B1321",
                      fontWeight: "600",
                      fontSize: "14px",
                    }}
                  >
                    Registration Closing Date
                  </div>
                  <div
                    style={{
                      fontSize: "22px",
                      fontWeight: "bold",
                      marginTop: "8px",
                      color: "#000",
                    }}
                  >
                    {closingDate}
                  </div>
                </div>
              )}
            </div>

            <div className="auto-container">
              <div className="outer-box">
                <div className="row">
                  {prices?.map((price) => (
                    <div
                      className="pricing-block-two col-md-6 col-sm-12"
                      key={price.id}
                    >
                      <SinglePriceV2 price={price} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        );

      case "committee":
        return (
          <section className={`pricing-section-two ${"alternate"}`}>
            <div className="sec-title  text-center">
              <h2
                style={{
                  display: "inline-block",
                  borderBottom: "2px solid #9B1321",
                  paddingBottom: "10px",
                }}
              >
                Committees
              </h2>
            </div>

            <div className="anim-icons">
              <span className="icon icon-line-1"></span>
              <span className="icon icon-circle-1"></span>
              <span className="icon icon-dots"></span>
            </div>

            <div className="auto-container">
              <div className="outer-box committee-card">
                <Box sx={{ padding: 2 }}>
                  {committee_members && committee_members.length > 0 && (
                    <>
                      {[...new Set(committee_members.map((m) => m.committee_type))].map(
                        (type, typeIndex) => (
                          <div key={typeIndex} style={{ marginBottom: "40px" }}>
                            <Typography
                              variant="h5"
                              sx={{
                                fontWeight: "bold",
                                color: "#9B1321",
                                mb: 3,
                                textAlign: "center",
                                borderBottom: "2px solid #9B1321",
                                paddingBottom: "10px",
                                display: "inline-block",
                              }}
                            >
                              {type || "General Committee"}
                            </Typography>

                            <Grid container spacing={3}>
                              {committee_members
                                .filter((member) => member.committee_type === type)
                                .map((member) => {
                                  const flagUrl = getFlagUrl(member.country);
                                  return (
                                    <Grid
                                      item
                                      xs={12}
                                      sm={6}
                                      md={3}
                                      lg={3}
                                      key={member.id}


                                      className="GridImage"
                                    >
                                      <Card
                                        sx={{
                                          borderRadius: 3,
                                          overflow: "hidden",
                                          boxShadow: 3,
                                          flex: 1,
                                          height: "100%",
                                          position: "relative",
                                        }}
                                      >
                                        <CardMedia
                                          component="img"
                                          height="250"
                                          image={makeStorageUrl(member?.committee_image)}
                                          alt={member?.name}
                                          sx={{ objectFit: "cover" }}
                                        />

                                        {flagUrl && (
                                          <Box
                                            sx={{
                                              position: "absolute",
                                              top: 8,
                                              right: 8,
                                              width: 32,
                                              height: 24,
                                              borderRadius: 1,
                                              overflow: "hidden",
                                              border: "2px solid white",
                                              boxShadow: 2,
                                              zIndex: 10,
                                            }}
                                          >
                                            <img
                                              src={flagUrl}
                                              alt={`${member.country} flag`}
                                              style={{
                                                width: "100%",
                                                height: "100%",
                                                objectFit: "cover",
                                              }}
                                            />
                                          </Box>
                                        )}

                                        <CardContent
                                          sx={{
                                            textAlign: "center",
                                            padding: 3,
                                          }}
                                        >
                                          <Typography
                                            variant="h6"
                                            sx={{
                                              fontWeight: "bold",
                                              color: "#333",
                                            }}
                                          >
                                            {member?.name}
                                          </Typography>
                                          <Typography
                                            variant="h6"
                                            sx={{
                                              fontWeight: "normal",
                                              color: "#333",
                                              marginTop:'20',
                                              fontSize: '12px',
                                            }}
                                          >
                                           ({member?.title})
                                          </Typography>
                                        </CardContent>
                                      </Card>
                                    </Grid>
                                  );
                                })}
                            </Grid>
                          </div>
                        )
                      )}
                    </>
                  )}
                </Box>
              </div>
            </div>
          </section>
        );

      //  overview and workspace
      case "firstAnnouncement":
case "secondAnnouncement":
case "brochure":
case "scientificProgram": {
  const rawKey = sections[selectedTab].url;
  const finalUrl = makeStorageUrl(data?.[rawKey]);
  return renderDocumentContent(finalUrl, sections[selectedTab].label);
}

      case "accommodation":
        return (
          <Box>
            {loading ? (
              <Typography variant="h6" sx={{ textAlign: "center", color: "gray" }}>
                Loading...
              </Typography>
            ) : error ? (
              <Typography variant="h6" sx={{ color: "red", textAlign: "center" }}>
                {error}
              </Typography>
            ) : (
              <AccommodationPrices roomPrices={roomPrices} />
            )}
          </Box>
        );

      case "conferenceBook": {
        const firstBook =
          conferenceBooks && conferenceBooks.length > 0 ? conferenceBooks[0] : null;

        const conferenceBookUrl = makeStorageUrl(firstBook?.pdf_file);
        return (
          <section className={`pricing-section-two ${"alternate"}`}>
            <div className="anim-icons">
              <span className="icon icon-line-1"></span>
              <span className="icon icon-circle-1"></span>
              <span className="icon icon-dots"></span>
            </div>
            <div className="auto-container">
              <div className="outer-box">
                {conferenceBookLoading ? (
                  <Typography variant="h6" sx={{ textAlign: "center", color: "gray" }}>
                    Loading conference book...
                  </Typography>
                ) : conferenceBookError ? (
                  <Typography variant="h6" sx={{ textAlign: "center", color: "red" }}>
                    {conferenceBookError}
                  </Typography>
                ) : !firstBook || !conferenceBookUrl ? (
                  <Typography variant="body1" sx={{ textAlign: "center" }}>
                    No conference book available.
                  </Typography>
                ) : (
                  <Paper
                    elevation={3}
                    sx={{
                      maxWidth: 600,
                      margin: "0 auto",
                      padding: 4,
                      textAlign: "center",
                      borderRadius: 3,
                    }}
                  >
                    <Typography variant="h5" sx={{ mt: 2, fontWeight: "bold" }}>
                      Conference Book
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 1, mb: 2 }}>
                      The conference book contains all accepted abstracts and program
                      details. Please download it using the button below.
                    </Typography>
                    <Button
                      variant="contained"
                      sx={{ mt: 2, backgroundColor: "#9B1321" }}
                      href={conferenceBookUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Download Conference Book
                    </Button>
                  </Paper>
                )}
              </div>
            </div>
          </section>
        );
      }

      case "posters":
        return (
          <section className={`pricing-section-two ${"alternate"}`}>
            <div className="anim-icons">
              <span className="icon icon-line-1"></span>
              <span className="icon icon-circle-1"></span>
              <span className="icon icon-dots"></span>
            </div>
            <div className="auto-container">
              <div className="outer-box">
                {postersLoading ? (
                  <Typography variant="h6" sx={{ textAlign: "center", color: "gray" }}>
                    Loading posters...
                  </Typography>
                ) : postersError ? (
                  <Typography variant="h6" sx={{ textAlign: "center", color: "red" }}>
                    {postersError}
                  </Typography>
                ) : posters.length === 0 ? (
                  <Typography variant="body1" sx={{ textAlign: "center" }}>
                    No posters available.
                  </Typography>
                ) : (
                  <Paper
                    elevation={3}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      overflowX: "auto",
                    }}
                    style={{ padding: "0px" }}
                  >
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        minWidth: 800,
                      }}
                    >
                      <thead>
                        <tr>
                          <th style={thStyle}>Id</th>
                          <th style={thStyle}>Name</th>
                          <th style={thStyle}>Title</th>
                          <th style={thStyle}>Abstract</th>
                          <th style={thStyle}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {posters.map((p, idx) => {
                          const fileUrl = makeStorageUrl(p.file_path);

                          return (
                            <tr key={p.id || idx}>
                              <td style={tdStyle}>{idx + 1}</td>
                              <td style={{ ...tdStyle, fontWeight: 600 }}>{p.name || "-"}</td>
                              <td style={{ ...tdStyle, fontWeight: 600 }}>{p.title || "-"}</td>
                              <td
                                style={{
                                  ...tdStyle,
                                  maxWidth: 300,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                                title={p.abstract}
                              >
                                {p.abstract || "-"}
                              </td>
                              <td style={tdStyle}>
                                {fileUrl ? (
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    sx={{ borderColor: "#9B1321", color: "#9B1321" }}
                                    onClick={() => window.open(fileUrl, "_blank")}
                                  >
                                    View
                                  </Button>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    No file
                                  </Typography>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </Paper>
                )}
              </div>
            </div>
          </section>
        );

      default:
        return <Typography variant="body1">Section not found.</Typography>;
    }
  };

  // styles used in posters table (to keep your code same)
  const thStyle = {
    borderBottom: "1px solid #e5e7eb",
    textAlign: "left",
    padding: "10px",
    background: "#9B1321",
    color: "#fff",
  };

  const tdStyle = {
    borderBottom: "1px solid #f3f4f6",
    padding: "8px",
  };

  return (
    <div
      sx={{
        marginTop: "15vh",
        padding: "10px",
       
      }}
    >
      {/* (Optional) If you use InnerBanner in your page, keep it here */}
      {/* <InnerBanner /> */}




      {/* ✅ COUNTDOWN TOP */}
      {/* <Box sx={{ marginTop: "120px", display: "flex", justifyContent: "center" }}>
        <Paper
          elevation={2}
          sx={{
            px: 3,
            py: 1.5,
            borderRadius: 3,
            border: "1px solid #9B1321",
            backgroundColor: "#FFF5F5",
            textAlign: "center",
            minWidth: { xs: "90%", sm: 520 },
          }}
        >
          <Typography sx={{ color: "#9B1321", fontWeight: 700, fontSize: 14 }}>
            Conference ends in
          </Typography>

          {countdownError ? (
            <Typography sx={{ mt: 0.5, color: "error.main", fontWeight: 600 }}>
              {countdownError}
            </Typography>
          ) : !countdownTarget ? (
            <Typography sx={{ mt: 0.5, color: "text.secondary", fontWeight: 600 }}>
              Loading...
            </Typography>
          ) : countdown.done ? (
            <Typography sx={{ mt: 0.5, color: "#000", fontWeight: 800, fontSize: 22 }}>
              Started / Expired
            </Typography>
          ) : (
            <Typography sx={{ mt: 0.5, color: "#000", fontWeight: 800, fontSize: 22 }}>
              {String(countdown.d).padStart(2, "0")}d : {String(countdown.h).padStart(2, "0")}h :{" "}
              {String(countdown.m).padStart(2, "0")}m : {String(countdown.s).padStart(2, "0")}s
            </Typography>
          )}
        </Paper>
      </Box> */}

      <Box sx={{ marginTop: "4vh", display: "flex", alignItems: "center" }}>
        <Tabs
          value={value}
          onChange={handleChange}
          variant="scrollable"
          allowScrollButtonsMobile
          scrollButtons="auto"
          aria-label="tabs"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          sx={{
            "& .MuiTabs-flexContainer": {
              display: "flex",
              position: "relative",
            },
            "& .MuiTabs-scroller": {
              overflowX: "auto",
              scrollBehavior: stopScroll ? "auto" : "smooth",
              transition: stopScroll ? "none" : "scroll-left 20s ease-in-out",
            },
            "& .MuiTabs-scrollButtons": {
              color: "red",
              fontSize: "30px",
              animation: "pulse 1.5s infinite ease-in-out",
            },
            "& .MuiTabs-scrollButtons.Mui-disabled": {
              opacity: 0.3,
            },
            "@keyframes pulse": {
              "0%": { transform: "scale(1)" },
              "50%": { transform: "scale(1.3)" },
              "100%": { transform: "scale(1)" },
            },
          }}
        >
          {sections.map((section, index) => (
            <Tab key={index} label={section.label} sx={{ fontWeight: "600" }} />
          ))}
        </Tabs>
      </Box>

      <Box
        sx={{
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {renderContent()}
      </Box>
    </div>
  );
};

export default ConferenceDetails;